import { useEffect, useMemo, useState } from "react";
import { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import { Trash2Icon, FileTextIcon, ArchiveXIcon } from "lucide-react";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    BirthdayFundRequest,
    FundRequestStatus,
    useDeleteRequest,
    useListBirthdayRequests,
} from "@/lib/api/fund";
import { formatCurrency, GenerateLoadingOrError } from "@/lib/utils";
import useDebounce from '../../otc/useDebounce';
import { getReadableStatusLabel, getStatusBadgeColor } from "../common";
import { BirthdayFundSummary } from "./BirthdayFundSummary";
import { ByMonthSummary } from "./summaries/monthSummary";
import { ByOrganizationSummary } from "./summaries/organization";
import { BirthdayFundInsights } from "./summaries/insights";
import { ViewBirthdayFundRequest } from "./BirthdayFundModal";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Dialog, DialogTitle, DialogContent, DialogHeader, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { CommonDestinationTimezone } from "@/lib/utils";
import { AssociateCertificateModal } from "./AssociateCertificateModal";
import { BirthdayFundGrantedExportButton } from "./BirthdayFundGrantedExportReport";
import { ExpireRequestConfirmDialog } from "./ExpireRequestConfirmDialog";
import { cn } from "@/lib/utils";

// Extend the type to include guardian fields that exist at runtime but aren't in the base type
type ExtendedBirthdayFundRequest = BirthdayFundRequest & {
    survey: BirthdayFundRequest['survey'] & {
        data: BirthdayFundRequest['survey']['data'] & {
            guardian_first_name?: string;
            guardian_phone_number?: string;
        }
    }
};

type RequestFilter = "all" | "new_requests" | "granted" | "expired";

// Define the filter types
const RequestStatusFilterValidation = z.object({
    status: z.array(z.nativeEnum(FundRequestStatus)).optional(),
    dispersed: z.boolean().optional(),
    search: z.string().optional(),
});

type FiltersState = z.infer<typeof RequestStatusFilterValidation>;

export function BirthdayFundRequestsDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentTab, setTab] = useState<"requests" | "customer_summary" | "by_month_summary" | "insights">(searchParams.get("tab") as any || 'requests');

    // Initialize filters from URL parameters
    const [filters, setFilters] = useState<FiltersState>(() => {
        const initialFilters: FiltersState = {};

        const statusParams = searchParams.getAll('status');
        if (statusParams.length > 0) {
            initialFilters.status = statusParams as FundRequestStatus[];
        }

        const dispersedParam = searchParams.get('dispersed');
        if (dispersedParam) {
            initialFilters.dispersed = dispersedParam === 'true';
        }

        const searchParam = searchParams.get('search');
        if (searchParam) {
            initialFilters.search = searchParam;
        }

        return initialFilters;
    });

    const [grantedFilter, setGrantedFilter] = useState<RequestFilter>(() => {
        const param = searchParams.get('granted_filter');
        if (param === 'all' || param === 'granted') return param;
        return 'new_requests';
    });

    const debouncedFilters = useDebounce(filters, 300);

    // Always fetch all requests — granted filtering is done client-side
    const birthdayListResult = useListBirthdayRequests({ filters: debouncedFilters });
    const allRequests = birthdayListResult?.data?.data?.fund_requests || [];

    // Handle tab change
    const onTabChange = (tab: string) => {
        setTab(tab as any);
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tab', tab);
            return newParams;
        });
    };

    const onGrantedFilterChange = (value: RequestFilter) => {
        setGrantedFilter(value);
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('granted_filter', value);
            return newParams;
        });
    };

    // Active requests (non-expired) for summary tabs
    const activeRequests = allRequests.filter((r: BirthdayFundRequest) => !r.expired_at);

    const loadingOrErrorUi = GenerateLoadingOrError([birthdayListResult]);
    if (loadingOrErrorUi) return loadingOrErrorUi;

    return (
        <div className="space-y-4">
            <ViewBirthdayFundRequest />
            <BirthdayFundSummary data={birthdayListResult?.data?.data} />
            <Tabs value={currentTab} onValueChange={onTabChange}>
                <TabsList className="mb-4">
                    <TabsTrigger value="requests">Requests</TabsTrigger>
                    <TabsTrigger value="customer_summary">Organization Summary</TabsTrigger>
                    <TabsTrigger value="by_month_summary">Month Summary</TabsTrigger>
                    <TabsTrigger value="insights">Insights</TabsTrigger>
                </TabsList>

                <TabsContent value="requests">
                    <BirthdayFundList
                        allRequests={allRequests}
                        grantedFilter={grantedFilter}
                        onGrantedFilterChange={onGrantedFilterChange}
                    />
                </TabsContent>
                <TabsContent value="customer_summary">
                    <ByOrganizationSummary requests={activeRequests} />
                </TabsContent>
                <TabsContent value="by_month_summary">
                    <ByMonthSummary requests={activeRequests} />
                </TabsContent>
                <TabsContent value="insights">
                    <BirthdayFundInsights data={birthdayListResult?.data?.data} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function BirthdayFundList({
    allRequests,
    grantedFilter,
    onGrantedFilterChange,
}: {
    allRequests: BirthdayFundRequest[];
    grantedFilter: RequestFilter;
    onGrantedFilterChange: (value: RequestFilter) => void;
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const deleteRequest = useDeleteRequest();
    const [requestToDelete, setDeleteRequest] = useState<BirthdayFundRequest | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [requestIdToAssociateCertificate, setRequestIdToAssociateCertificate] = useState<string | null>(null);
    const [requestToExpire, setRequestToExpire] = useState<BirthdayFundRequest | null>(null);

    const filteredRequests = useMemo(() => {
        if (grantedFilter === "expired") return allRequests.filter((r: BirthdayFundRequest) => !!r.expired_at);
        const activeLocalRequests = allRequests.filter((r: BirthdayFundRequest) => !r.expired_at);
        if (grantedFilter === 'all') return activeLocalRequests;
        if (grantedFilter === 'granted') return activeLocalRequests.filter(r => r.has_been_granted);
        return activeLocalRequests.filter(r => !r.has_been_granted);
    }, [allRequests, grantedFilter]);

    const setSpecificParam = (paramName: string, paramValue: string) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set(paramName, paramValue);
            return newParams;
        });
    };

    const openDeleteRequestModal = (request: BirthdayFundRequest) => {
        setDeleteRequest(request);
    };

    const openAssociateCertificateModal = (request: BirthdayFundRequest) => {
        setRequestIdToAssociateCertificate(request.id);
    };

    // Look up the fresh request from the list by ID
    const requestToAssociateCertificate = requestIdToAssociateCertificate
        ? filteredRequests.find(r => r.id === requestIdToAssociateCertificate) || null
        : null;

    const onOpenViewRequest = (request_id: string) => {
        setSpecificParam("request_id", request_id);
    };

    const onDeleteRequest = async (request: BirthdayFundRequest) => {
        try {
            if (!request || isDeleting) return;
            setIsDeleting(true);

            const result = await deleteRequest({ id: request.id });
            if (!result.success) {
                throw result;
            }

            setDeleteRequest(null);
            toast({ title: "Deleted" });
        } catch (error) {
            console.error("Failed to delete fund request", error);
            toast({ title: "Error deleting request", variant: "destructive" });
        } finally {
            setIsDeleting(false);
        }
    };

    const top_level_columns: DataTableColumn<ExtendedBirthdayFundRequest>[] = [
        {
            id: "birthday_fund_number",
            accessorKey: "birthday_fund_number",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.birthday_fund_number,
            header: "Birthday Fund ID",
            cell: (info) => {
                const value = info.getValue() as number;
                return value ? `#${value}` : "-";
            },
        },
        {
            id: "nominator",
            accessorKey: "nominator",
            accessorFn: (row: ExtendedBirthdayFundRequest) => `${row.survey.data.nominator_first_name || ""} ${row.survey.data.nominator_last_name || ""}`.trim(),
            header: "Nominator",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "email",
            accessorKey: "email",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.email || "",
            header: "Email",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "guardian_first_name",
            accessorKey: "guardian_first_name",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.guardian_first_name || "",
            header: "Guardian First Name",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "guardian_phone_number",
            accessorKey: "guardian_phone_number",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.guardian_phone_number || "",
            header: "Guardian Phone Number",
            cell: (info) => {
                const phoneNumber = info.getValue() as string;
                return phoneNumber ? new PhoneNumberFormatterAsYouType('US').input(phoneNumber) : "";
            },
        },
        {
            id: "nominated_organization",
            accessorKey: "nominated_organization",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.nominated_organization || "",
            header: "Nominated Organization",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "nominating_organization",
            accessorKey: "nominating_organization",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.nominating_organization || "",
            header: "Survey Nominating Organization",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "organization",
            accessorKey: "organization",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.organization || "",
            header: "Organization",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "birthday",
            accessorKey: "birthday",
            accessorFn: (row: ExtendedBirthdayFundRequest) => [row.survey.data.birthday_month_of_child, row.survey.data.birthday_day_of_child].filter(Boolean).join(" "),
            header: "Birthday",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "age",
            accessorKey: "age",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.child_age || "N/A",
            header: "Age",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "location",
            accessorKey: "location",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.survey.data.party_location || "",
            header: "Location",
            cell: (info) => info.getValue() as string,
        },
        {
            id: "status",
            accessorKey: "status",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.status,
            header: "Status",
            cell: ({ row }) => (
                <Badge className={getStatusBadgeColor(row.original.status)}>
                    {getReadableStatusLabel(row.original.status)}
                </Badge>
            ),
        },
        {
            id: "granted",
            accessorKey: "granted",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.has_been_granted ? "Yes" : "No",
            header: "Granted",
            cell: ({ row }) => (
                <>{row.original.has_been_granted ? "Yes" : "No"}</>
            ),
        },
        {
            id: "amount_projected",
            accessorKey: "amount_projected",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.amount_projected,
            header: "Projected Amount",
            cell: (info) => {
                const value = info.getValue();
                return value ? formatCurrency(parseFloat(value as string)) : "-";
            },
        },
        {
            id: "amount_granted",
            accessorKey: "amount_granted",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.amount_granted,
            header: "Granted Amount",
            cell: (info) => {
                const value = info.getValue();
                return value ? formatCurrency(parseFloat(value as string)) : "-";
            },
        },
        {
            id: "has_been_dispersed",
            accessorKey: "has_been_dispersed",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.has_been_dispersed ? "Yes" : "No",
            header: "Disbursed",
            cell: ({ row }) => (
                <>{row.original.has_been_dispersed ? "Yes" : "No"}</>
            ),
        },
        {
            id: "has_been_reconciled",
            accessorKey: "has_been_reconciled",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.has_been_reconciled ? "Yes" : "No",
            header: "Reconciled",
            cell: ({ row }) => (
                <>{row.original.has_been_reconciled ? "Yes" : "No"}</>
            ),
        },
        {
            id: "expired_at",
            accessorKey: "expired_at",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.expired_at || "",
            header: "Expired At",
            cell: (info) => {
                const value = info.getValue() as string;
                return value ? moment.tz(value, CommonDestinationTimezone).format("MM/DD/YYYY") : "-";
            },
        },
        {
            id: "actions",
            accessorKey: "actions",
            header: "Actions",
            disableRowClick: true,
            cell: ({ row }) => (
                <div className="flex gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openAssociateCertificateModal(row.original)}
                        title="Associate Certificate"
                    >
                        <FileTextIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteRequestModal(row.original)}
                        title="Delete Request"
                    >
                        <Trash2Icon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setRequestToExpire(row.original)}
                        title="Mark as Expired"
                        disabled={!!row.original.expired_at}
                    >
                        <ArchiveXIcon className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            accessorFn: (row: ExtendedBirthdayFundRequest) => row.created_at,
            columnFilterType: 'date',
            filterDateTimezone: CommonDestinationTimezone,
            filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
            filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
            header: "Created At",
            cell: (info) => (
                <span className="text-gray-600 text-sm">
                    {moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")}
                </span>
            ),
        },
    ];

    const toggleOptions: { label: string; value: RequestFilter }[] = [
        { label: "All", value: "all" },
        { label: "New Requests", value: "new_requests" },
        { label: "Granted", value: "granted" },
        { label: "Expired", value: "expired" },
    ];

    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-1 mx-4 items-center">
                {toggleOptions.map((option) => (
                    <Button
                        key={option.value}
                        variant="outline"
                        size="sm"
                        onClick={() => onGrantedFilterChange(option.value)}
                        className={cn(
                            grantedFilter === option.value && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                        )}
                    >
                        {option.label}
                    </Button>
                ))}
                {grantedFilter === 'granted' && (
                    <div className="ml-2">
                        <BirthdayFundGrantedExportButton requests={filteredRequests} />
                    </div>
                )}
            </div>
            <DataTable
                rowClickable
                onRowClick={(row, columnId) => {
                    if (columnId !== "actions") {
                        onOpenViewRequest(row.id);
                    }
                }}
                defaultSorting={[{ id: "created_at", desc: true }]}
                tableClassName="max-h-[680px] overflow-y-scroll"
                columns={top_level_columns}
                data={filteredRequests}
                className="mx-4"
                enableSorting
                enableMultiSort
                enableFiltering
                enableColumnVisibility
                enableGrouping
                enableExport
                enableVirtualization
                virtualizationRowHeight={65}
                virtualizationOverscan={2}
                exportFilename={() => [grantedFilter, moment().format("YYYYMMDD")].join("_")}
                stickyHeaders
                filterStyle="select"
                filterMode="internal"
                emptyMessage="No birthday fund requests found"
                persistColumnSettings
                tableId="birthday_fund_unified_requests"
                enableColumnPinning
                enableMultiSelect
            />
            {requestToDelete && (
                <DeleteRequestModal request={requestToDelete} onCancel={() => setDeleteRequest(null)} onDelete={() => onDeleteRequest(requestToDelete)} isDeleting={isDeleting} />
            )}
            <AssociateCertificateModal
                request={requestToAssociateCertificate}
                isOpen={!!requestIdToAssociateCertificate}
                onClose={() => setRequestIdToAssociateCertificate(null)}
            />
            <ExpireRequestConfirmDialog
                request={requestToExpire}
                open={!!requestToExpire}
                onOpenChange={(open) => { if (!open) setRequestToExpire(null); }}
            />
        </div>
    );
}


function DeleteRequestModal({ request, onCancel, onDelete, isDeleting }: { request: BirthdayFundRequest, onCancel: () => void, onDelete: () => void, isDeleting: boolean }) {
    return (
        <Dialog open={!!request} onOpenChange={onCancel}>
            <DialogContent className="md:max-w-2xl lg:max-w-3xl xl:max-w-4xl">
                <DialogHeader>
                    <DialogTitle>Delete Request</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this request?
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <div className="mb-4">
                        <div className="grid grid-cols-2 gap-3 bg-accent/40 rounded-lg p-4 border">
                            <div>
                                <div className="font-semibold text-accent-foreground mb-1">Nominator</div>
                                <div className="text-muted-foreground">{request.survey.data.nominator_first_name} {request.survey.data.nominator_last_name}</div>
                                <div className="font-semibold text-accent-foreground mt-3 mb-1">Organization</div>
                                <div className="text-muted-foreground">{request.survey.data.nominating_organization}</div>
                                <div className="font-semibold text-accent-foreground mt-3 mb-1">Email</div>
                                <div className="text-muted-foreground">{request.survey.data.email}</div>
                                <div className="font-semibold text-accent-foreground mt-3 mb-1">Phone</div>
                                <div className="text-muted-foreground">{request.survey.data.phone}</div>
                            </div>
                            <div>
                                <div className="font-semibold text-accent-foreground mb-1">Child's Birthday</div>
                                <div className="text-muted-foreground">{request.survey.data.birthday_month_of_child}/{request.survey.data.birthday_day_of_child} (Age: {request.survey.data.child_age})</div>
                                {request.survey.data.party_location && (
                                    <>
                                        <div className="font-semibold text-accent-foreground mt-3 mb-1">Party Location</div>
                                        <div className="text-muted-foreground">{request.survey.data.party_location}</div>
                                    </>
                                )}
                                {request.survey.data.notes && (
                                    <>
                                        <div className="font-semibold text-accent-foreground mt-3 mb-1">Notes</div>
                                        <div className="text-muted-foreground">{request.survey.data.notes}</div>
                                    </>
                                )}
                            </div>
                            <div className="col-span-2 border-t mt-3 pt-3 flex flex-wrap gap-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${request.status === "granted" ? "bg-green-100 text-green-800" : request.status === "awaiting_validation" ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"}`}>
                                    Status: {request.status.replace(/_/g, " ")}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${request.has_been_granted ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>
                                    Granted: {request.has_been_granted ? "Yes" : "No"}
                                </span>
                                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                    Amount: {request.amount_granted ? request.amount_granted : "-"}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${request.has_been_dispersed ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>
                                    Dispersed: {request.has_been_dispersed ? "Yes" : "No"}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${request.has_been_reconciled ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>
                                    Reconciled: {request.has_been_reconciled ? "Yes" : "No"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel}>Cancel</Button>
                    <Button variant="destructive" onClick={onDelete} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Confirm"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
